// Content input validation schemas using Zod

import { z } from 'zod';

// Re-export Google URL detection from the extraction module
export { isGoogleDocUrl, getGoogleDocType } from '../../lib/google-extraction';

export const MAX_CONTENT_LENGTH = 50_000;

// Maximum file size in bytes (10MB for documents)
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Maximum image size in bytes (5MB for images)
export const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

// Maximum number of images allowed per session
export const MAX_IMAGE_COUNT = 5;

// Supported document MIME types
export const SUPPORTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
] as const;

// Supported document extensions
export const SUPPORTED_FILE_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.txt'] as const;

// Supported image MIME types
export const SUPPORTED_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;

// Supported image extensions
export const SUPPORTED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'] as const;

/**
 * Validation schema for content input
 * - Required: content must not be empty
 * - Max length: 50,000 characters
 */
export const contentInputSchema = z.object({
  content: z
    .string()
    .min(1, { message: 'empty' })
    .max(MAX_CONTENT_LENGTH, { message: 'tooLong' }),
});

export type ContentInputData = z.infer<typeof contentInputSchema>;

/**
 * File validation error codes for i18n
 *
 * Note on error key naming:
 * - File (document) errors use generic names: 'unsupportedType', 'tooLarge'
 *   → Maps to creator.fileUpload.error.* i18n keys
 * - Image errors use prefixed names: 'unsupportedImageType', 'imageTooLarge', 'maxImagesExceeded'
 *   → Maps to creator.imageUpload.error.* i18n keys
 * This allows separate i18n namespaces with context-appropriate messages.
 */
export type FileValidationErrorCode = 'unsupportedType' | 'tooLarge';

/**
 * File validation result
 */
export interface FileValidationResult {
  isValid: boolean;
  errorCode?: FileValidationErrorCode;
}

/**
 * Validates a file for upload
 * @param file - The file to validate
 * @returns Validation result with error code if invalid
 */
export function validateFile(file: File): FileValidationResult {
  // Check file type
  if (!SUPPORTED_FILE_TYPES.includes(file.type as typeof SUPPORTED_FILE_TYPES[number])) {
    // Also check by extension as fallback
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!SUPPORTED_FILE_EXTENSIONS.includes(extension as typeof SUPPORTED_FILE_EXTENSIONS[number])) {
      return { isValid: false, errorCode: 'unsupportedType' };
    }
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { isValid: false, errorCode: 'tooLarge' };
  }

  return { isValid: true };
}

/**
 * Validates multiple files and returns all validation errors
 */
export function validateFiles(files: File[]): Map<string, FileValidationErrorCode> {
  const errors = new Map<string, FileValidationErrorCode>();

  files.forEach((file) => {
    const result = validateFile(file);
    if (!result.isValid && result.errorCode) {
      errors.set(file.name, result.errorCode);
    }
  });

  return errors;
}

/**
 * Image validation error codes for i18n
 */
export type ImageValidationErrorCode =
  | 'unsupportedImageType'
  | 'imageTooLarge'
  | 'maxImagesExceeded';

/**
 * Image validation result
 */
export interface ImageValidationResult {
  isValid: boolean;
  errorCode?: ImageValidationErrorCode;
}

/**
 * Checks if a file is an image based on MIME type or extension
 */
export function isImageFile(file: File): boolean {
  if (SUPPORTED_IMAGE_TYPES.includes(file.type as (typeof SUPPORTED_IMAGE_TYPES)[number])) {
    return true;
  }
  // Fallback to extension check
  const extension = '.' + file.name.split('.').pop()?.toLowerCase();
  return SUPPORTED_IMAGE_EXTENSIONS.includes(
    extension as (typeof SUPPORTED_IMAGE_EXTENSIONS)[number]
  );
}

/**
 * Validates an image file for upload
 * @param file - The image file to validate
 * @returns Validation result with error code if invalid
 */
export function validateImage(file: File): ImageValidationResult {
  // Check if it's actually an image
  if (!isImageFile(file)) {
    return { isValid: false, errorCode: 'unsupportedImageType' };
  }

  // Check image size (5MB max)
  if (file.size > MAX_IMAGE_SIZE) {
    return { isValid: false, errorCode: 'imageTooLarge' };
  }

  return { isValid: true };
}

/** Maps clipboard image MIME type to file extension */
export function mimeToExtension(mime: string): string {
  switch (mime) {
    case 'image/png': return '.png';
    case 'image/jpeg': return '.jpg';
    case 'image/webp': return '.webp';
    default: return '.png';
  }
}

/** Creates a File from a clipboard Blob with an auto-generated name */
export function createPastedImageFile(blob: Blob): File {
  const ext = mimeToExtension(blob.type);
  const timestamp = Math.floor(Date.now() / 1000);
  const name = `pasted-image-${timestamp}${ext}`;
  return new File([blob], name, { type: blob.type });
}

/**
 * Validates image count against maximum allowed
 * @param currentCount - Current number of images already uploaded
 * @param newCount - Number of new images being added
 * @returns Validation result with error code if limit exceeded
 */
export function validateImageCount(
  currentCount: number,
  newCount: number = 1
): ImageValidationResult {
  if (currentCount + newCount > MAX_IMAGE_COUNT) {
    return { isValid: false, errorCode: 'maxImagesExceeded' };
  }
  return { isValid: true };
}

/**
 * Validates multiple images and returns all validation errors
 * @param files - Array of image files to validate
 * @param currentImageCount - Current number of images already uploaded
 */
export function validateImages(
  files: File[],
  currentImageCount: number = 0
): Map<string, ImageValidationErrorCode> {
  const errors = new Map<string, ImageValidationErrorCode>();

  // First check if adding these files would exceed the limit
  const countResult = validateImageCount(currentImageCount, files.length);
  if (!countResult.isValid && countResult.errorCode) {
    // Mark the first file with the count error
    const firstFile = files[0];
    if (firstFile) {
      errors.set(firstFile.name, countResult.errorCode);
    }
    return errors;
  }

  // Validate each individual image
  files.forEach((file) => {
    const result = validateImage(file);
    if (!result.isValid && result.errorCode) {
      errors.set(file.name, result.errorCode);
    }
  });

  return errors;
}
